import {useEffect, useMemo} from 'react';
import {makeAutoObservable, IReactionDisposer, autorun, reaction, when, isObservableObject} from 'mobx';
import {IAutorunOptions} from "mobx";

type AutoRunEffect<T extends object> = (obj: T) => void;
type ReactionEffect = (newValue: any, oldValue: any) => void;
type WhenEffect = () => void;
type ReactionExpression<T> = (obj: T) => any;
type WhenCondition = (obj: any) => boolean;

interface UseMobxOptions<T extends object> {
    autoruns?: (AutoRunEffect<T> | [AutoRunEffect<T>, IAutorunOptions])[];
    reactions?: { effect: ReactionEffect; expression: ReactionExpression<T> }[];
    whens?: { effect: WhenEffect; condition: WhenCondition }[];
    dependencies?: any[];
}

export const useMobx = <T extends object>(getObj: () => T, {
    autoruns = [],
    reactions = [],
    whens = [],
    dependencies = [],
}: UseMobxOptions<T> = {}): T => {
    /* eslint-disable react-hooks/exhaustive-deps */
    const state = useMemo(() => {
        const o = getObj();
        if (!isObservableObject(o)) {
            return makeAutoObservable(o);
        } else {
            return o;
        }
    }, dependencies);
    /* eslint-enable react-hooks/exhaustive-deps */

    useEffect(() => {
        const disposers: IReactionDisposer[] = [];

        for (const autoRunEffect of autoruns) {
            if (Array.isArray(autoRunEffect)) {
                const [effect, options] = autoRunEffect;
                const disposer = autorun(() => effect(state), options);
                disposers.push(disposer);
            } else {
                const disposer = autorun(() => autoRunEffect(state));
                disposers.push(disposer);
            }
        }

        for (const {effect, expression} of reactions) {
            const disposer = reaction(() => expression(state), effect);
            disposers.push(disposer);
        }

        for (const {effect, condition} of whens) {
            const disposer = when(() => condition(state), () => effect());
            disposers.push(disposer);
        }

        return () => {
            for (const disposer of disposers) {
                disposer();
            }
        };
    }, [state, autoruns, reactions, whens]);

    return state;
};
